import { NavLink, Outlet } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../context/AuthContext.js';
import { useAppConfig } from '../context/AppConfigContext.js';
import { useCart } from '../context/CartContext.js';
import { TrainingStrip } from './TrainingStrip.js';

export function AppShell() {
  const { user, logout } = useAuth();
  const { environment } = useAppConfig();
  const cart = useCart();

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <div className="sticky top-0 z-40">
        <TrainingStrip />
        <header className="bg-teal text-white shadow-sm">
          <div className="page-container flex items-center justify-between gap-4 h-14 lg:h-16">
            <div className="flex items-center gap-8 min-w-0">
              {/* "Divergent Works" is much longer than the old wordmark, so it
                  steps down a size below `sm` and never wraps - see the
                  rebrand spec. text-xl/text-2xl bold-ish Poppins 600 both
                  clear WCAG AA "large text" white-on-teal (~4.03:1) - see the
                  QA report, P3 #15. */}
              <span className="font-heading font-semibold text-xl sm:text-2xl whitespace-nowrap">
                Divergent Works
              </span>
              {environment === 'training' && (
                <span
                  className="hidden sm:inline-flex items-center rounded-full px-3 py-1 font-heading font-semibold text-sm whitespace-nowrap"
                  style={{ backgroundColor: 'var(--training-strip)', color: 'var(--ink)' }}
                >
                  TRAINING
                </span>
              )}
              <nav
                aria-label="Primary"
                className="hidden lg:flex items-center gap-1 rounded-xl bg-teal-dark p-1"
              >
                <TopTabLink to="/sell" label="Sell" />
                <TopTabLink to="/today" label="Today" />
                {user?.role === 'admin' && <TopTabLink to="/admin" label="Admin" />}
              </nav>
            </div>
            <div className="flex items-center gap-3">
              {/* bg-teal-dark chip (not plain white-on-teal) so this 14px/600
                  name clears WCAG AA on its own - plain --teal only passes at
                  >=18.67px bold, see the QA report, P3 #15 / reverify partial. */}
              <span className="font-heading font-semibold text-sm hidden sm:inline bg-teal-dark text-white rounded-full px-3 py-1">
                {user?.displayName}
              </span>
              <button
                type="button"
                // Explicit logout clears the in-progress cart (unlike an
                // idle-timeout redirect, which must leave it in
                // sessionStorage so a sales person who signs back in still
                // has their items - see lib/api.ts's SESSION_EXPIRED
                // handling).
                onClick={() => {
                  cart.clear();
                  void logout();
                }}
                // teal-dark (not the white/15-over-teal overlay) so white text
                // at a normal 14px passes AA on its own, and min-h-12 for a
                // proper >=48px tap target - see the QA report, P2 #9 / P3 #15.
                className="min-h-12 px-4 rounded-lg bg-teal-dark font-heading font-semibold text-sm text-white hover:opacity-90 whitespace-nowrap"
              >
                Log out
              </button>
            </div>
          </div>
        </header>
      </div>

      <main className="flex-1 pb-20 lg:pb-0">
        <div className="page-container">
          <Outlet />
        </div>
      </main>

      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-surface border-t border-border"
        aria-label="Primary"
      >
        <div
          className={clsx(
            'page-container !px-0 grid',
            user?.role === 'admin' ? 'grid-cols-3' : 'grid-cols-2',
          )}
        >
          <TabLink to="/sell" label="Sell" />
          <TabLink to="/today" label="Today" />
          {user?.role === 'admin' && <TabLink to="/admin" label="Admin" />}
        </div>
      </nav>
    </div>
  );
}

function TabLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          'flex flex-col items-center justify-center min-h-16 font-body font-semibold text-sm border-t-[3px]',
          isActive ? 'text-teal-dark border-teal-dark' : 'text-muted border-transparent',
        )
      }
    >
      {label}
    </NavLink>
  );
}

function TopTabLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          'min-h-12 px-5 rounded-lg flex items-center font-heading font-semibold text-base',
          isActive ? 'bg-surface text-teal-dark' : 'text-white hover:bg-white/10',
        )
      }
    >
      {label}
    </NavLink>
  );
}
